// Settings: the same groups as the mobile app's Settings screen, plus a
// Profile group for name, business details, and region.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { IconType } from "react-icons";
import {
  BiArrowBack,
  BiBadgeCheck,
  BiChevronRight,
  BiCreditCard,
  BiGroup,
  BiLockAlt,
  BiSliderAlt,
  BiUser,
  BiUserCircle,
} from "react-icons/bi";
import { getActiveWorkspaceId } from "../../services/workspaceApi";
import { setActiveCurrency } from "../../services/currency";
import {
  AccountSettings,
  Me,
  getAccountSettings,
  getMe,
} from "../../services/settingsApi";
import ProfileSection from "./sections/ProfileSection";
import BillingSection from "./sections/BillingSection";
import TeamSection from "./sections/TeamSection";
import PreferencesSection from "./sections/PreferencesSection";
import SecuritySection from "./sections/SecuritySection";
import AccountSection from "./sections/AccountSection";

type GroupKey = "profile" | "billing" | "team" | "preferences" | "security" | "account";

const GROUPS: Array<{
  key: GroupKey;
  label: string;
  icon: IconType;
  tint: string;
  /** Shown under the label on the Settings home. */
  blurb: string;
}> = [
  {
    key: "profile",
    label: "Profile",
    icon: BiUserCircle,
    tint: "#0EA5E9",
    blurb: "Your name, business details, country and currency",
  },
  {
    key: "billing",
    label: "Billing & payments",
    icon: BiCreditCard,
    tint: "#3366FF",
    blurb: "Plan, Square, card fee, payment methods, taxes",
  },
  {
    key: "team",
    label: "Team",
    icon: BiGroup,
    tint: "#5E5CE6",
    blurb: "Employees, invitations, who you work for",
  },
  {
    key: "preferences",
    label: "Preferences",
    icon: BiSliderAlt,
    tint: "#28A745",
    blurb: "Default dashboard, reminders, time zone, emails",
  },
  {
    key: "security",
    label: "Security",
    icon: BiLockAlt,
    tint: "#F5A524",
    blurb: "Finance passcode and password",
  },
  {
    key: "account",
    label: "Account",
    icon: BiUser,
    tint: "#FF375F",
    blurb: "Archive your account",
  },
];

const TIER_LABEL: Record<string, string> = {
  none: "Free plan",
  lite: "Lite plan",
  pro: "Pro plan",
  elite: "Elite plan",
};

const initialsOf = (text: string) =>
  text
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const SettingsPage = () => {
  const [params, setParams] = useSearchParams();
  const requested = params.get("section");
  const active = GROUPS.find((g) => g.key === requested) ?? null;

  const [me, setMe] = useState<Me | null>(null);
  const [settings, setSettings] = useState<AccountSettings | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [meData, settingsData] = await Promise.all([getMe(), getAccountSettings()]);
      setMe(meData);
      setSettings(settingsData);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [active?.key]);

  const open = (key: GroupKey | null) => setParams(key ? { section: key } : {});

  // Employee working in an employer's business: billing is theirs, read-only.
  const activeWorkspaceId = getActiveWorkspaceId();
  const workspace =
    me?.workspaces.find((w) => w.id === activeWorkspaceId) ??
    me?.workspaces.find((w) => w.id === me.defaultWorkspaceId);
  const workingForEmployer = workspace?.isOwn === false;
  const employerName = workingForEmployer ? workspace?.name : undefined;
  const tier = me?.tier || "none";
  const canUseEmployees = tier === "pro" || tier === "elite";

  const header = (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-blue-600">Settings</p>
      <h1 className="text-3xl font-bold tracking-tight text-slate-900">
        {active ? active.label : "Settings"}
      </h1>
    </div>
  );

  let body: JSX.Element;
  if (error) {
    body = (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        Couldn't load your settings.
        <button type="button" onClick={load} className="ml-3 font-semibold underline">
          Try again
        </button>
      </div>
    );
  } else if (!me || !settings) {
    body = (
      <div className="space-y-4">
        <div className="h-32 animate-pulse rounded-2xl bg-white" />
        <div className="h-72 animate-pulse rounded-2xl bg-white" />
      </div>
    );
  } else if (!active) {
    const displayName = settings.businessName || settings.name || "Your business";
    body = (
      <div className="space-y-5">
        <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-800 p-5 text-white shadow-sm md:p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 text-xl font-bold">
              {initialsOf(settings.businessName || settings.name || settings.email)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-lg font-bold">{displayName}</p>
              <p className="truncate text-sm text-white/80">
                {settings.businessName && settings.name ? `${settings.name} · ` : ""}
                {settings.email}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1">
              <BiBadgeCheck size={14} />
              {TIER_LABEL[tier] || "Free plan"}
            </span>
            {workingForEmployer && (
              <span className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-white/20 px-2.5 py-1">
                Working for {employerName || "an employer"}
              </span>
            )}
          </div>
        </div>

        <nav className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {GROUPS.map((group, index) => {
            const Icon = group.icon;
            const blurb =
              group.key === "billing" && workingForEmployer
                ? `${employerName || "Your employer"}'s payment settings`
                : group.blurb;
            return (
              <button
                key={group.key}
                type="button"
                onClick={() => open(group.key)}
                className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition hover:bg-slate-50 md:px-5 ${
                  index > 0 ? "border-t border-slate-100" : ""
                }`}
              >
                <span
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${group.tint}1F`, color: group.tint }}
                >
                  <Icon size={20} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-slate-900">{group.label}</span>
                  <span className="block truncate text-sm text-slate-500">{blurb}</span>
                </span>
                <BiChevronRight size={22} className="flex-shrink-0 text-slate-400" />
              </button>
            );
          })}
        </nav>
      </div>
    );
  } else {
    body = (
      <>
        {active.key === "profile" && (
          <ProfileSection
            settings={settings}
            onSaved={(next) => {
              setSettings(next);
              // Your own business's currency changes everywhere right away.
              if (!workingForEmployer) setActiveCurrency(next.currency);
            }}
          />
        )}
        {active.key === "billing" && (
          <BillingSection
            tier={tier}
            workingForEmployer={workingForEmployer}
            employerName={employerName}
          />
        )}
        {active.key === "team" && (
          <TeamSection
            userId={me._id}
            canUseEmployees={canUseEmployees}
            showOwnerTools={!workingForEmployer}
            // Joining or leaving a business changes the switcher in the nav.
            onTeamChanged={() => window.location.reload()}
          />
        )}
        {active.key === "preferences" && (
          <PreferencesSection settings={settings} onSaved={setSettings} />
        )}
        {active.key === "security" && <SecuritySection />}
        {active.key === "account" && <AccountSection email={settings.email} />}
      </>
    );
  }

  return (
    <div className="min-h-[calc(100vh-56px)] bg-slate-50/90 px-4 py-8 md:px-8">
      <div className="mx-auto max-w-3xl">
        {active && (
          <button
            type="button"
            onClick={() => open(null)}
            className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            <BiArrowBack size={16} /> All settings
          </button>
        )}
        {header}
        <div className="mt-6">{body}</div>
      </div>
    </div>
  );
};

export default SettingsPage;
